import { Fragment, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { AnomalyLine, AnomalyRow, NeverConnectedRow } from '@evi/shared';
import { useDashboardAnomalies } from '@/hooks/useDashboardAnomalies';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { siteDetailPath } from '@/lib/siteDashboard';
import {
  ANOMALY_TYPE_LABELS, formatSince, lastActionCell, lineLink, longDownBadge, networkFailuresLine, platformEventLine, reconnectsCell, rebootsCell, rowKey, rowLink, rowTitle,
  startSuccessCell, utcTime, waitingCell, watchNote, type Cell,
} from '@/lib/anomalyView';
import styles from '../chargers/ChargersPage.module.css';
import own from './AnomaliesPage.module.css';

/**
 * /anomalies: every anomaly in the caller's scope, grouped by site, newest onset first (Warren, 28 Oct 2026, addendum
 * item 3: "Show real reboots and reconnects separately, leave out drops within 10 minutes of our own roll or crash and
 * network events, and group by site so a whole site down is one row. Add to each row the last automatic action and
 * its result, and who we're waiting on (building, installer, EVI)"; item 2: start success on this page).
 *
 * The same rows as the dashboard widget, from the same 30 s cache (`?limit=all`). A site row opens and closes its
 * chargers' lines; a line, or a row of one charger, opens the charger. Chargers never seen on v3 are their own section, and chargers that are not live are only counted. The
 * platform events and the ours/network split are EVI's alone: the Api leaves them out for everyone else, and this page
 * shows what it was sent.
 */
export function AnomaliesPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  useRecentRoute('Anomalies');
  const isSite = useSiteDashboard();
  const q = useDashboardAnomalies('all');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const data = q.data;
  const rows = data?.rows ?? [];
  const never = data?.never_connected ?? [];
  const now = new Date();
  const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><AlertTriangle size={20} /></div>
          <div>
            <h1 className={styles.title}>Anomalies</h1>
            <p className={styles.subtitle}>
              Faulted, offline, rebooting, losing connection, and sites starting below 98%, grouped by site, newest first.
              {data ? ` ${data.total_count} open${data.charger_count ? ` (${data.charger_count} charger${data.charger_count === 1 ? '' : 's'})` : ''}.` : ''}
            </p>
          </div>
        </div>
      </div>

      {q.isError && <div className={styles.error}>Could not load anomalies.</div>}

      {data?.platform_events && (data.platform_events.length > 0 || data.start_failures_network || (data.watch_last_at && !data.watch_since)) && (
        <ul className={own.events} aria-label="Platform events">
          <li className={own.eventsTitle}>Platform events, last 24 hours (EVI only)</li>
          {data.platform_events.map((e) => <li key={`${e.kind}-${e.started_at}`}>{platformEventLine(e)}</li>)}
          {data.start_failures_network && <li>{networkFailuresLine(data.start_failures_network)}</li>}
          <li className={own.eventsNote}>
            Boots, drops and reconnects within 10 minutes of these are not counted against a charger. {watchNote(data.watch_since, data.watch_last_at)}
          </li>
        </ul>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Severity</th>
              <th>Charger or site</th>
              <th>What</th>
              <th>Since</th>
              <th>Reboots 24h</th>
              <th>Reconnects 24h</th>
              <th>Start success 7d</th>
              <th>Last automatic action</th>
              <th>Waiting on</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && !data ? (
              <tr><td colSpan={9} className={styles.loading}>Loading...</td></tr>
            ) : rows.length === 0 && !q.isError ? (
              <tr>
                <td colSpan={9} className={styles.empty}>
                  <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  All chargers operating normally.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const lines = row.lines ?? [];
                const expanded = !!open[key];
                return (
                  <Fragment key={key}>
                    <AnomalyTableRow
                      row={row}
                      now={now}
                      expanded={expanded}
                      onToggle={lines.length ? () => toggle(key) : undefined}
                      onOpen={() => (lines.length ? toggle(key) : onNavigate(rowLink(row, isSite, siteDetailPath)))}
                    />
                    {expanded && lines.map((l, i) => (
                      // A site can have several site-wide lines of one type (a regression per signal): the index keeps each key its own.
                      <LineRow key={`${key}-${l.charger_id}-${l.anomaly_type}-${i}`} line={l} onOpen={() => onNavigate(lineLink(l, row, isSite, siteDetailPath))} />
                    ))}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {never.length > 0 && <NeverConnected rows={never} now={now} onOpen={(r) => onNavigate(isSite ? siteDetailPath(r.location_id) : `/chargers/${r.charger_id}`)} />}

      {data && (data.not_live_count ?? 0) > 0 && (
        <p className={own.note} style={{ marginTop: 16 }}>
          {data.not_live_count} charger{data.not_live_count === 1 ? ' is' : 's are'} left out: switched off, at a switched-off site, commissioning, or in a maintenance window.
        </p>
      )}
    </div>
  );
}

function CellView({ cell }: { cell: Cell }) {
  const tone = cell.tone === 'bad' ? own.bad : cell.tone === 'amber' ? own.amber : cell.tone === 'muted' ? own.muted : undefined;
  return (
    <>
      <span className={`${own.cellMain} ${tone ?? ''}`}>{cell.text}</span>
      {cell.detail && <span className={own.cellDetail}>{cell.detail}</span>}
    </>
  );
}

function SeverityBadge({ severity }: { severity: AnomalyRow['severity'] }) {
  return (
    <span
      className={styles.onlineBadge}
      style={severity === 'critical'
        ? { background: 'rgba(255,59,48,0.12)', color: '#ff3b30' }
        : { background: 'rgba(217,119,6,0.12)', color: '#d97706' }}
    >
      {severity === 'critical' ? 'Critical' : 'Warning'}
    </span>
  );
}

function AnomalyTableRow({ row, now, expanded, onToggle, onOpen }: {
  row: AnomalyRow; now: Date; expanded: boolean; onToggle?: () => void; onOpen: () => void;
}) {
  const t = rowTitle(row);
  const badge = longDownBadge(row);
  return (
    <tr className={styles.row} onClick={onOpen}>
      <td><SeverityBadge severity={row.severity} /></td>
      <td>
        {onToggle && (
          <button
            type="button"
            className={own.caret}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide chargers' : 'Show chargers'}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        )}
        <span className={styles.identity}>{t.title}</span>
        {t.sub && <span className={own.cellDetail}>{t.sub}</span>}
      </td>
      <td>
        <span className={own.cellMain}>{ANOMALY_TYPE_LABELS[row.anomaly_type]}: {row.description}</span>
        {row.platform_note && <span className={own.platformNote}>{row.platform_note}</span>}
        {row.handler_errors_1h ? <span className={own.cellDetail}>{row.handler_errors_1h} error replies from our side in the last hour</span> : null}
      </td>
      <td className={styles.dimText}>
        {formatSince(row.duration_ms)}
        {badge && <span className={own.badge}>{badge}</span>}
      </td>
      <td><CellView cell={rebootsCell(row.boots)} /></td>
      <td><CellView cell={reconnectsCell(row.reconnects_24h)} /></td>
      <td><CellView cell={startSuccessCell(row.start_success)} /></td>
      <td><CellView cell={lastActionCell(row.last_action, now)} /></td>
      <td><CellView cell={waitingCell(row.waiting_on)} /></td>
    </tr>
  );
}

function LineRow({ line, onOpen }: { line: AnomalyLine; onOpen: () => void }) {
  return (
    <tr className={`${styles.row} ${own.line}`} onClick={onOpen}>
      <td><SeverityBadge severity={line.severity} /></td>
      <td className={own.lineName}><span className={styles.identity}>{line.charger_identity || 'The site'}</span></td>
      <td colSpan={7}>
        <span className={own.cellMain}>{ANOMALY_TYPE_LABELS[line.anomaly_type]}: {line.description}</span>
        {line.platform_note && <span className={own.platformNote}>{line.platform_note}</span>}
        {line.last_seen && <span className={own.cellDetail}>Last seen {utcTime(line.last_seen)} on {new Date(line.last_seen).toLocaleDateString()}</span>}
      </td>
    </tr>
  );
}

function NeverConnected({ rows, now, onOpen }: { rows: NeverConnectedRow[]; now: Date; onOpen: (r: NeverConnectedRow) => void }) {
  return (
    <div className={own.section}>
      <h2 className={own.sectionTitle}>Never connected to v3 ({rows.length})</h2>
      <p className={own.note}>Registered on v3 and never seen on it: no heartbeat and no message, ever. Nothing went down, so these are not in the table above.</p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Charger</th>
              <th>Location</th>
              <th>Registered</th>
              <th>Last automatic action</th>
              <th>Waiting on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.charger_id} className={styles.row} onClick={() => onOpen(r)}>
                <td>
                  <span className={styles.identity}>{r.charger_identity}</span>
                  {r.display_name && <span className={own.cellDetail}>{r.display_name}</span>}
                </td>
                <td className={styles.dimText}>{r.location_name ?? 'No location'}</td>
                <td className={styles.dimText}>
                  {r.registered_at ? `${formatSince(now.getTime() - Date.parse(r.registered_at))} ago` : 'Unknown'}
                </td>
                <td><CellView cell={lastActionCell(r.last_action, now)} /></td>
                <td><CellView cell={waitingCell(r.waiting_on)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
